import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, Tabs, TabPanel } from '@/backoffice/components/ui';
import { AdminGeographyManager } from '@/backoffice/components/admin/AdminGeographyManager';
import { LocalizationInputGroup, type LocalizationValue } from '@/backoffice/components/forms/LocalizationInputGroup';
import { useAdminContext } from '@/backoffice/hooks/useAdminContext';
import {
  fetchRelayPoints,
  createRelayPoint,
  deleteRelayPoint,
} from '@/domains/admin/services/admin.service';
import { createRelayPointActivity, fetchRelayPointActivities } from '@/domains/api/messaging.api';
import type { AdminRelayPoint } from '@/domains/admin/services/admin.service';
import { Building2, Plus, Trash2 } from 'lucide-react';

export function AdminInfrastructurePage() {
  const userType = useAdminContext();
  const [relayPoints, setRelayPoints] = useState<AdminRelayPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [relayForm, setRelayForm] = useState({
    name: '', address: '', manager_name: '', manager_phone: '', opening_hours: '',
  });
  const [relayLoc, setRelayLoc] = useState<LocalizationValue>({
    countryId: null, regionId: null, prefectureId: null, communeId: null, districtId: null, secteurId: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRelayId, setSelectedRelayId] = useState<number | null>(null);
  const [relayActivities, setRelayActivities] = useState<Array<{ id: number; event_type: string; notes?: string | null; actor_name?: string | null; created_at?: string | null }>>([]);
  const [activityForm, setActivityForm] = useState({ event_type: 'deposit', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRelayPoints(await fetchRelayPoints());
    } catch {
      setError('Impossible de charger les points relais.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadRelayActivities = async (relayId: number) => {
    try {
      setRelayActivities(await fetchRelayPointActivities(relayId));
    } catch {
      setRelayActivities([]);
    }
  };

  const handleSelectRelay = (relayId: number) => {
    setSelectedRelayId(relayId);
    loadRelayActivities(relayId);
  };

  const handleLogActivity = async () => {
    if (!selectedRelayId) return;
    setSaving(true);
    try {
      await createRelayPointActivity(selectedRelayId, activityForm);
      setActivityForm({ event_type: 'deposit', notes: '' });
      await loadRelayActivities(selectedRelayId);
    } catch {
      setError('Impossible d\'enregistrer l\'activité.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRelay = async () => {
    if (!relayForm.name.trim() || !relayForm.address.trim()) return;
    setSaving(true);
    try {
      await createRelayPoint({
        name: relayForm.name,
        address: relayForm.address,
        secteur_id: relayLoc.secteurId,
        manager_name: relayForm.manager_name || null,
        manager_phone: relayForm.manager_phone || null,
        opening_hours: relayForm.opening_hours || null,
        is_active: true,
      });
      setRelayForm({ name: '', address: '', manager_name: '', manager_phone: '', opening_hours: '' });
      setRelayLoc({ countryId: null, regionId: null, prefectureId: null, communeId: null, districtId: null, secteurId: null });
      await load();
    } catch {
      setError('Échec de la création du point relais.');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: 'geography', label: 'Géographie' },
    { key: 'relay', label: 'Points relais', badge: relayPoints.length },
  ];

  return (
    <DashboardLayout userType={userType} title="Infrastructures">
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <Tabs tabs={tabs}>
        <TabPanel>
          <AdminGeographyManager />
        </TabPanel>
        <TabPanel>
          <Card className="mb-6">
            <h3 className="font-semibold mb-4">Nouveau point relais</h3>
            <LocalizationInputGroup value={relayLoc} onChange={setRelayLoc} className="mb-4" />
            <div className="grid md:grid-cols-2 gap-4">
              <Input label="Nom" value={relayForm.name} onChange={(e) => setRelayForm((f) => ({ ...f, name: e.target.value }))} />
              <Input label="Adresse" value={relayForm.address} onChange={(e) => setRelayForm((f) => ({ ...f, address: e.target.value }))} />
              <Input label="Responsable" value={relayForm.manager_name} onChange={(e) => setRelayForm((f) => ({ ...f, manager_name: e.target.value }))} />
              <Input label="Téléphone" value={relayForm.manager_phone} onChange={(e) => setRelayForm((f) => ({ ...f, manager_phone: e.target.value }))} />
              <Input label="Horaires" value={relayForm.opening_hours} onChange={(e) => setRelayForm((f) => ({ ...f, opening_hours: e.target.value }))} />
            </div>
            <Button className="mt-4" leftIcon={<Plus className="w-4 h-4" />} isLoading={saving} onClick={handleCreateRelay}>Ajouter</Button>
          </Card>
          {loading ? <p className="text-center text-gray-500 py-8">Chargement...</p> : (
            <div className="grid md:grid-cols-2 gap-4">
              {relayPoints.map((rp) => (
                <Card key={rp.id} className={selectedRelayId === rp.id ? 'border-indigo-400' : ''}>
                  <div className="flex justify-between">
                    <button type="button" className="flex gap-3 text-left flex-1" onClick={() => handleSelectRelay(rp.id)}>
                      <Building2 className="w-5 h-5 text-indigo-600 shrink-0" />
                      <div>
                        <p className="font-medium">{rp.name}</p>
                        <p className="text-sm text-gray-600">{rp.address}</p>
                        <p className="text-xs text-gray-500">{rp.zone_name ?? '—'} · {rp.opening_hours}</p>
                        <p className="text-xs text-indigo-600 mt-1">Voir l'historique d'activité</p>
                      </div>
                    </button>
                    <button type="button" onClick={async () => { await deleteRelayPoint(rp.id); load(); }} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
          {selectedRelayId && (
            <Card className="mt-6">
              <h3 className="font-semibold mb-4">Historique du point relais</h3>
              <div className="grid md:grid-cols-3 gap-3 mb-4">
                <Input label="Type d'événement" value={activityForm.event_type} onChange={(e) => setActivityForm((f) => ({ ...f, event_type: e.target.value }))} />
                <Input label="Notes" value={activityForm.notes} onChange={(e) => setActivityForm((f) => ({ ...f, notes: e.target.value }))} className="md:col-span-2" />
              </div>
              <Button size="sm" className="mb-4" isLoading={saving} onClick={handleLogActivity}>Enregistrer une activité</Button>
              {relayActivities.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune activité enregistrée.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {relayActivities.map((ev) => (
                    <div key={ev.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <p className="font-medium">{ev.event_type}</p>
                      {ev.notes && <p className="text-gray-600">{ev.notes}</p>}
                      <p className="text-xs text-gray-400 mt-1">{ev.actor_name ?? 'Système'} · {ev.created_at ? new Date(ev.created_at).toLocaleString('fr-FR') : ''}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </TabPanel>
      </Tabs>
    </DashboardLayout>
  );
}
